import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material'
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material'
import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth'
import { userAtom, isLoadingAtom, User } from '../state/auth'
import { usersApi, SESSION_NOTICE_KEY } from '../services/api'
import { colors } from '../theme'
import { awsConfig } from '../config/aws'
import BrandLogo from '../components/BrandLogo'

const API_BASE_URL = awsConfig.API.REST.missfit.endpoint

export default function Login() {
  const navigate = useNavigate()
  const setUser = useSetAtom(userAtom)
  const setIsLoading = useSetAtom(isLoadingAtom)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // MWA-286 — an expired session redirects here; api.ts leaves the reason in
  // sessionStorage because the hard navigation destroys any in-page handler.
  // Read it once on mount and clear it, so it does not resurface on a later
  // manual visit to /login.
  const [sessionNotice, setSessionNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')

  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState<'idle' | 'request' | 'confirm'>('idle')
  const [resetEmail, setResetEmail] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [resetNewPassword, setResetNewPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')

  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(SESSION_NOTICE_KEY)
      if (notice) {
        setSessionNotice(notice)
        sessionStorage.removeItem(SESSION_NOTICE_KEY)
      }
    } catch {
      // Storage unavailable — the sign-in form still works without the notice.
    }
  }, [])

  const loadUserAndNavigate = async () => {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken
    if (idToken) {
      const payload = idToken.payload
      const user: User = {
        email: (payload.email as string) || '',
        role: (payload['custom:role'] as User['role']) || 'sales',
        name: payload.name as string | undefined,
        // Only populated for client-role portal users. Portal sub-pages
        // (Interviews, Job Apps, Networking) gate their data loads on this,
        // so a missing value leaves them stuck on skeletons forever.
        clientId: payload['custom:clientId'] as string | undefined,
        isManager: payload['custom:isManager'] === 'true',
      }
      // Also check DB record for isManager in case Cognito attribute hasn't propagated
      if (user.role !== 'client') {
        try {
          const users = await usersApi.list()
          const dbUser = users.find((u) => u.email === user.email)
          if (dbUser?.isManager) {
            user.isManager = true
          }
        } catch {
          // Non-critical
        }
      }
      setUser(user)
    }
    // After a successful login we definitively have a valid session — flip the
    // global loading flag so ProtectedPortalRoute / ProtectedRoute don't keep
    // showing a full-screen spinner if App.tsx's mount-time checkSession hasn't
    // completed yet (e.g., fetchAuthSession({ forceRefresh: true }) stalled).
    setIsLoading(false)
    // MWA-362 — replace, so Back from the first page does not return to the
    // sign-in form.
    navigate('/', { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await signIn({ username: email, password })

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true)
        setLoading(false)
        return
      }

      if (result.isSignedIn) {
        await loadUserAndNavigate()
      }
    } catch (err: unknown) {
      const error = err as Error
      if (error.name === 'UserAlreadyAuthenticatedException') {
        await loadUserAndNavigate()
        return
      }
      setError(error.message || 'Failed to sign in')
    } finally {
      setLoading(false)
    }
  }

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword })
      if (result.isSignedIn) {
        await loadUserAndNavigate()
      }
    } catch (err: unknown) {
      const error = err as Error
      setError(error.message || 'Failed to set new password')
    } finally {
      setLoading(false)
    }
  }

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request password reset')
      }

      setSuccess('If an account exists with this email, you will receive a password reset code.')
      setForgotPasswordMode('confirm')
    } catch (err: unknown) {
      const error = err as Error
      setError(error.message || 'Failed to request password reset')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (resetNewPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/confirm-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail,
          code: resetCode,
          newPassword: resetNewPassword,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password')
      }

      setSuccess('Password has been reset successfully. You can now sign in with your new password.')
      // Reset all forgot password state
      setForgotPasswordMode('idle')
      setResetEmail('')
      setResetCode('')
      setResetNewPassword('')
      setResetConfirmPassword('')
    } catch (err: unknown) {
      const error = err as Error
      setError(error.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setForgotPasswordMode('idle')
    setError('')
    setSuccess('')
    setResetEmail('')
    setResetCode('')
    setResetNewPassword('')
    setResetConfirmPassword('')
  }

  // Forgot Password - Request Code View
  if (forgotPasswordMode === 'request') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.gray700} 100%)`,
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleBackToLogin} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                Reset Password
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a code to reset your password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleRequestReset}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                sx={{ mb: 3 }}
                autoComplete="email"
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !resetEmail}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  // Forgot Password - Confirm Code View
  if (forgotPasswordMode === 'confirm') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.gray700} 100%)`,
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleBackToLogin} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                Enter Reset Code
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the 6-digit code sent to {resetEmail} and your new password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleConfirmReset}>
              <TextField
                label="Reset Code"
                fullWidth
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
                inputProps={{ maxLength: 6 }}
                placeholder="123456"
              />
              <TextField
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={resetConfirmPassword}
                onChange={(e) => setResetConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !resetCode || !resetNewPassword || !resetConfirmPassword}
                sx={{ py: 1.5, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
              <Button
                variant="text"
                fullWidth
                onClick={() => setForgotPasswordMode('request')}
                sx={{ textTransform: 'none' }}
              >
                Didn't receive a code? Send again
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (needsNewPassword) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.gray700} 100%)`,
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please set a new password to continue.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleNewPassword}>
              <TextField
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Password'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.gray700} 100%)`,
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {/* Lockup carries the wordmark, so the name is not repeated below it */}
            <BrandLogo height={44} sx={{ mx: 'auto', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Sign in to manage your workflow
            </Typography>
          </Box>

          {sessionNotice && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {sessionNotice}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              autoComplete="email"
              autoFocus
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setForgotPasswordMode('request')}
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                Forgot password?
              </Link>
            </Box>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || !email || !password}
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
